// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { Calendar, Users, DollarSign, Plus, Edit, Ticket, TrendingUp } from 'lucide-react';
import { format } from 'date-fns';
import { parseLocalDate } from '@/utils/formatters';
import { toast } from 'sonner';

interface Event {
  id: string;
  slug: string;
  name: string;
  start_date: string;
  end_date: string;
  status: 'draft' | 'published' | 'archived';
  venue_name?: string;
  venue_city?: string;
  venue_state?: string;
  registration_type: 'team' | 'individual';
  registration_count?: number;
}

interface OrgStats {
  totalEvents: number;
  publishedEvents: number;
  totalRegistrations: number;
  totalRevenue: number;
}

const OrgAdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [stats, setStats] = useState<OrgStats>({
    totalEvents: 0,
    publishedEvents: 0,
    totalRegistrations: 0,
    totalRevenue: 0,
  });
  const [loading, setLoading] = useState(true);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [organizationName, setOrganizationName] = useState<string>('Organization');

  useEffect(() => {
    if (profile?.id) {
      fetchOrganizationAndData();
    }
  }, [profile?.id]);

  const fetchOrganizationAndData = async () => {
    if (!profile?.id) return;

    try {
      setLoading(true);

      // Get organization_id from organization_members (RLevents schema)
      const { data: roleData, error: roleError } = await (supabase as any)
        .from('organization_members')
        .select('organization_id, role')
        .eq('user_id', profile.id)
        .eq('status', 'active')
        .in('role', ['org_admin', 'org_staff'])
        .maybeSingle();

      if (roleError) {
        console.error('Error fetching role:', roleError);
        toast.error('Failed to load organization data');
        setLoading(false);
        return;
      }

      if (!roleData?.organization_id) {
        toast.error('No organization assigned to this account');
        setLoading(false);
        return;
      }

      setOrganizationId(roleData.organization_id);

      // Fetch organization name
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .select('name')
        .eq('id', roleData.organization_id)
        .single();

      if (orgError) {
        console.error('Error fetching organization:', orgError);
      } else if (orgData) {
        setOrganizationName(orgData.name);
      }

      // Now fetch dashboard data
      await fetchDashboardData(roleData.organization_id);

    } catch (error) {
      console.error('Error in fetchOrganizationAndData:', error);
      toast.error('Failed to load dashboard');
      setLoading(false);
    }
  };

  const fetchDashboardData = async (orgId: string) => {
    if (!orgId) return;

    try {
      // Fetch events for this organization
      const { data: eventsData, error: eventsError } = await supabase
        .from('events')
        .select('id, slug, name, start_date, end_date, status, venue_name, venue_city, venue_state, registration_type, organization_id, is_archived')
        .eq('organization_id', orgId)
        .is('deleted_at', null)
        .eq('is_archived', false)
        .order('start_date', { ascending: true });

      if (eventsError) throw eventsError;

      // Fetch registration counts for each event
      const eventsWithCounts = await Promise.all(
        (eventsData || []).map(async (event) => {
          const { count } = await supabase
            .from('event_registrations')
            .select('*', { count: 'exact', head: true })
            .eq('event_id', event.id);
          
          return {
            ...event,
            registration_count: count || 0,
          };
        })
      );

      setEvents(eventsWithCounts);

      // Calculate stats
      const totalEvents = eventsData?.length || 0;
      const publishedEvents = eventsData?.filter(e => e.status === 'published').length || 0;

      // Fetch registrations count
      const eventIds = eventsData?.map(e => e.id) || [];
      let totalRegistrations = 0;
      let totalRevenue = 0;

      if (eventIds.length > 0) {
        const { data: registrations, error: regError } = await supabase
          .from('event_registrations')
          .select('id, total_amount')
          .in('event_id', eventIds)
          .eq('payment_status', 'paid');

        if (regError) throw regError;

        totalRegistrations = registrations?.length || 0;
        totalRevenue = registrations?.reduce((sum, reg) => sum + (reg.total_amount || 0), 0) || 0;
      }

      setStats({
        totalEvents,
        publishedEvents,
        totalRegistrations,
        totalRevenue,
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(parseLocalDate(dateString), 'MMM d, yyyy');
    } catch {
      return 'Date TBD';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      draft: { label: 'Draft', className: 'bg-gray-100 text-gray-800' },
      published: { label: 'Published', className: 'bg-green-100 text-green-800' },
      archived: { label: 'Archived', className: 'bg-orange-100 text-orange-800' }
    };
    
    const badge = badges[status as keyof typeof badges] || badges.draft;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-muted-foreground">Loading dashboard...</p>
      </div>
    );
  }

  if (!organizationId) {
    return (
      <div className="p-4 bg-destructive/10 border-l-4 border-destructive text-destructive">
        <p className="font-bold">No Organization Assigned</p>
        <p>Your account is not linked to an organization. Please contact support.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading">{organizationName} Dashboard</h1>
          <p className="text-muted-foreground">Manage your organization's events and registrations</p>
        </div>
        <Button onClick={() => navigate('/admin-dashboard/events/new')} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Create Event
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Events</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalEvents}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.publishedEvents} published
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Registrations</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalRegistrations}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Across all events
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats.totalRevenue)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              From paid registrations
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Avg per Event</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.totalEvents > 0 ? formatCurrency(stats.totalRevenue / stats.totalEvents) : '$0'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Average revenue
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Events List */}
      <Card>
        <CardHeader>
          <CardTitle>Your Events</CardTitle>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">No events found for your organization.</p>
              <Button onClick={() => navigate('/admin-dashboard/events/new')} variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Event
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {events.map((event) => (
                <div key={event.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold">{event.name}</h3>
                        {getStatusBadge(event.status)}
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>📅 {formatDate(event.start_date)}</span>
                        {event.venue_city && (
                          <span>📍 {event.venue_city}, {event.venue_state}</span>
                        )}
                        <span className="capitalize">{event.registration_type}</span>
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {event.registration_count || 0} registrations
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => navigate(`/org-admin/events/${event.slug}`)}
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button 
              variant="outline" 
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => navigate('/admin-dashboard/events/new')}
            >
              <Plus className="h-6 w-6" />
              <div className="text-center">
                <div className="font-medium">Create Event</div>
                <div className="text-xs text-muted-foreground">Set up a new event</div>
              </div>
            </Button>
            
            <Button 
              variant="outline" 
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => navigate('/admin-dashboard/events')}
            >
              <Calendar className="h-6 w-6" />
              <div className="text-center">
                <div className="font-medium">Manage Events</div>
                <div className="text-xs text-muted-foreground">View all events</div>
              </div>
            </Button>
            
            <Button 
              variant="outline" 
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => toast.info('Reports coming soon')}
            >
              <DollarSign className="h-6 w-6" />
              <div className="text-center">
                <div className="font-medium">View Reports</div>
                <div className="text-xs text-muted-foreground">Revenue & analytics</div>
              </div>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OrgAdminDashboard;